import React, { useState, useEffect, useReducer, useRef } from "react";
import { useHistory } from "react-router-dom";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Description,
  Done,
  DoneAll,
  Edit,
  ExpandMore,
  GetApp,
  InsertDriveFile,
  InsertEmoticon,
  PictureAsPdf,
  Search as SearchIcon,
  TableChart,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import EditMessageDialog from "../EditMessageDialog";
import MessageReactionPicker from "../MessageReactionPicker";
import ForwardMessagesDialog from "../ForwardMessagesDialog";
import CallPreview from "../CallPreview";
import MessageAvatar from "../MessageAvatar";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import notificationService from "../../services/notificationService";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "flex-end",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  messageLeftContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    marginLeft: 8,
    position: "relative",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    height: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  messageRightBubbleWrap: {
    position: "relative",
    alignSelf: "flex-end",
    "&::before": {
      content: '""',
      position: "absolute",
      right: "100%",
      width: 56,
      top: 0,
      bottom: 0,
    },
    "&:hover #editMessageButton": {
      display: "flex",
    },
    "&:hover #reactionMessageButton": {
      display: "flex",
    },
  },

  messageEditButtonFloating: {
    display: "none",
    position: "absolute",
    right: "100%",
    marginRight: 4,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#999",
    zIndex: 1,
    backgroundColor: "transparent",
    padding: 4,
    "&:hover, &.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,0.06)" },
  },

  messageReactionButtonFloating: {
    display: "none",
    position: "absolute",
    right: "100%",
    marginRight: 36,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#999",
    zIndex: 1,
    backgroundColor: "transparent",
    padding: 4,
    "&:hover, &.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,0.06)" },
  },

  messageLeftBubbleWrap: {
    position: "relative",
    display: "inline-block",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "100%",
      width: 32,
      top: 0,
      bottom: 0,
    },
    "&:hover #reactionMessageButtonLeft": {
      display: "flex",
    },
  },

  messageReactionButtonLeft: {
    display: "none",
    position: "absolute",
    left: "100%",
    marginLeft: 4,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#999",
    zIndex: 1,
    backgroundColor: "transparent",
    padding: 4,
    "&:hover, &.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,0.06)" },
  },

  messageReactionsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    fontSize: 13,
  },

  /** Reactions float below the bubble, slightly on top of the bottom edge (WhatsApp-style) */
  messageReactionsFloating: {
    marginTop: -4,
    marginBottom: 2,
    minHeight: 20,
    display: "flex",
    alignItems: "center",
  },

  messageReactionsFloatingLeft: {
    justifyContent: "flex-start",
    marginLeft: 8,
  },

  messageReactionsFloatingRight: {
    justifyContent: "flex-start",
    marginLeft: 0,
    marginRight: 8,
    position: "relative",
    zIndex: 2,
    width: "100%",
  },

  reactionBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 6px",
    borderRadius: 12,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },

  messageRightContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    position: "relative",
    marginRight: 8,
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },


  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
    backgroundColor: "inherit",
    paddingLeft: 4,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  messageFileName: {
    fontSize: 13,
    fontWeight: 600,
    color: "inherit",
    marginBottom: 6,
    wordBreak: "break-word",
  },

  messageFileIcon: {
    fontSize: 36,
    marginRight: 12,
    flexShrink: 0,
    color: "#5f6368",
  },
  messageFileRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },


  forwardSelectionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    backgroundColor: "rgba(0,0,0,0.04)",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    flexShrink: 0,
  },

  forwardMessageRow: {
    display: "flex",
    alignItems: "flex-start",
    marginTop: 2,
    cursor: "pointer",
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.03)",
    },
  },

  forwardMessageRowRight: {
    justifyContent: "flex-end",
  },

  forwardMessageSelected: {
    backgroundColor: "rgba(25, 118, 210, 0.12)",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.18)",
    },
  },

  searchButtonFloating: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 10,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  messageHighlighted: {
    width: "100%",
    alignSelf: "stretch",
    marginLeft: -20,
    marginRight: -20,
    paddingLeft: 20,
    paddingRight: 20,
    boxSizing: "border-box",
    backgroundColor: "rgba(255, 235, 59, 0.7)",
    borderRadius: 8,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "REPLACE_MESSAGES") {
    return action.payload;
  }
};

const CtwaPreview = ({ context }) => {
  if (!context) return null;
  let ctx;
  try {
    ctx = typeof context === 'string' ? JSON.parse(context) : context;
  } catch {
    return null;
  }
  if (!ctx.sourceUrl && !ctx.title && !ctx.description && !ctx.thumbnailUrl && !ctx.thumbnail) return null;
  // Always prefer base64 thumbnail if present
  const thumbSrc = ctx.thumbnail
    ? `data:image/jpeg;base64,${ctx.thumbnail}`
    : ctx.thumbnailUrl;
  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      marginBottom: 6,
      padding: 8,
      background: '#f9f9f9',
      display: 'flex',
      alignItems: 'flex-start',
      maxWidth: 400
    }}>
      {thumbSrc && (
        <img
          src={thumbSrc}
          alt={ctx.title}
          style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginRight: 10 }}
        />
      )}
      <div style={{ flex: 1 }}>
        {ctx.title && <div style={{ fontWeight: 600, fontSize: 15 }}>{ctx.title}</div>}
        {ctx.description && <div style={{ color: '#555', fontSize: 13, margin: '4px 0' }}>{ctx.description}</div>}
        {ctx.sourceUrl && <a href={ctx.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontSize: 13, wordBreak: 'break-all' }}>{ctx.sourceUrl}</a>}
      </div>
    </div>
  );
};

const MessagesList = ({ ticketId, isGroup, focusMessageId, inChatSearchMessageId, onSearchClick }) => {
  const classes = useStyles();
  const history = useHistory();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState(null);
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState(null);
  const [messageForReaction, setMessageForReaction] = useState(null);
  const [ourReactionByMessageId, setOurReactionByMessageId] = useState({});
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardInitialSelectedIds, setForwardInitialSelectedIds] = useState([]);
  const [forwardSelectMode, setForwardSelectMode] = useState(false);
  const [forwardSelectedIds, setForwardSelectedIds] = useState(new Set());
  const [highlightMessageId, setHighlightMessageId] = useState(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState("");
  const currentTicketId = useRef(ticketId);
  const highlightClearTimeoutRef = useRef(null);

  const openPdfPreview = (url, title) => {
    setPdfPreviewUrl(url);
    setPdfPreviewTitle(title || "Preview");
    setPdfPreviewOpen(true);
  };

  const toggleForwardMessage = (id) => {
    setForwardSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const cancelForwardSelection = () => {
    setForwardSelectMode(false);
    setForwardSelectedIds(new Set());
    setAnchorEl(null);
  };

  const proceedToForwardRecipients = () => {
    if (forwardSelectedIds.size === 0) return;
    setForwardInitialSelectedIds(Array.from(forwardSelectedIds));
    setForwardDialogOpen(true);
    setForwardSelectMode(false);
    setForwardSelectedIds(new Set());
    setAnchorEl(null);
  };

  const wrapMessageForForward = (message, messageContent, alignRight = false) => {
    if (!forwardSelectMode) return messageContent;
    return (
      <div
        className={clsx(classes.forwardMessageRow, {
          [classes.forwardMessageRowRight]: alignRight,
          [classes.forwardMessageSelected]: forwardSelectedIds.has(message.id),
        })}
        onClick={() => toggleForwardMessage(message.id)}
      >
        <Checkbox
          checked={forwardSelectedIds.has(message.id)}
          disableRipple
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleForwardMessage(message.id)}
        />
        {messageContent}
      </div>
    );
  };

  useEffect(() => {
    if (highlightClearTimeoutRef.current) {
      clearTimeout(highlightClearTimeoutRef.current);
      highlightClearTimeoutRef.current = null;
    }
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHighlightMessageId(null);
    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const params = { pageNumber };
          if (pageNumber === 1 && focusMessageId) {
            params.messageId = focusMessageId;
          }
          const { data } = await api.get("/messages/" + ticketId, {
            params,
          });

          if (currentTicketId.current === ticketId) {
            // Save scroll position before loading more
            let messagesListDiv = document.getElementById("messagesList");
            let prevScrollHeight = messagesListDiv ? messagesListDiv.scrollHeight : 0;
            let prevScrollTop = messagesListDiv ? messagesListDiv.scrollTop : 0;

            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            if (data.focusMessageId) {
              if (highlightClearTimeoutRef.current) {
                clearTimeout(highlightClearTimeoutRef.current);
                highlightClearTimeoutRef.current = null;
              }
              setHighlightMessageId(data.focusMessageId);
              highlightClearTimeoutRef.current = setTimeout(() => {
                setHighlightMessageId(null);
                highlightClearTimeoutRef.current = null;
              }, 10000);
              history.replace(history.location.pathname);
            }
            // Log message order for debugging
            setTimeout(() => {
              const order = data.messages.map(m => m.createdAt);
              console.log("[MessagesList] Loaded page", pageNumber, "order:", order);
            }, 0);
            // Only scroll to bottom on first page when NOT focusing a specific message
            if (!data.focusMessageId && pageNumber === 1 && data.messages.length > 1) {
              scrollToBottom();
            } else if (pageNumber > 1 && messagesListDiv) {
              // Maintain scroll position after prepending older messages
              setTimeout(() => {
                if (messagesListDiv) {
                  messagesListDiv.scrollTop = messagesListDiv.scrollHeight - prevScrollHeight + prevScrollTop;
                }
              }, 0);
            }
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, focusMessageId, history]);

  const applyHighlightAndScroll = React.useCallback((msgId) => {
    const id = String(msgId);
    if (highlightClearTimeoutRef.current) {
      clearTimeout(highlightClearTimeoutRef.current);
      highlightClearTimeoutRef.current = null;
    }
    setHighlightMessageId(msgId);
    highlightClearTimeoutRef.current = setTimeout(() => {
      setHighlightMessageId(null);
      highlightClearTimeoutRef.current = null;
    }, 10000);
    const scrollToEl = () => {
      const el = document.querySelector(`[data-message-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToEl());
    });
    [50, 200, 400, 700].forEach((ms) => setTimeout(scrollToEl, ms));
  }, []);

  useEffect(() => {
    if (!highlightMessageId) return;
    const scrollToEl = () => {
      const el = document.querySelector(`[data-message-id="${highlightMessageId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    scrollToEl();
    const t1 = setTimeout(scrollToEl, 100);
    const t2 = setTimeout(scrollToEl, 300);
    const t3 = setTimeout(scrollToEl, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [highlightMessageId, messagesList]);

  const goToMessage = React.useCallback((msgId) => {
    const id = String(msgId);
    const isLoaded = messagesList.some((m) => String(m.id) === id);
    if (isLoaded) {
      applyHighlightAndScroll(msgId);
      return;
    }
    const fetchForMessage = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber: 1, messageId: msgId },
        });
        if (currentTicketId.current === ticketId) {
          dispatch({ type: "REPLACE_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);
          applyHighlightAndScroll(msgId);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchForMessage();
  }, [ticketId, messagesList, applyHighlightAndScroll]);

  useEffect(() => {
    if (!inChatSearchMessageId) return;
    goToMessage(inChatSearchMessageId);
  }, [inChatSearchMessageId, goToMessage]);

  useEffect(() => {
    if (!focusMessageId) return;
    goToMessage(focusMessageId);
  }, [focusMessageId, goToMessage]);

  useEffect(() => {
    const socket = openSocket();

    // Set current ticket for notification service
    notificationService.setCurrentTicket(ticketId);

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
        
        // Show notification for new message only if:
        // - Message is not from current user (unless system message)
        // - Tab is not visible or user is not viewing this ticket
        if (!data.message.fromMe || data.message.nameUser === 'SYSTEM' || data.message.nameUser === 'SISTEMA') {
          // Only notify if tab is hidden or user is viewing a different ticket
          if (document.visibilityState === 'hidden' || 
              !data.message.ticketId || 
              data.message.ticketId !== ticketId) {
            notificationService.handleNewMessage(data.message, data.ticket);
          }
        }
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    // Cleanup: reset current ticket when component unmounts
    return () => {
      notificationService.setCurrentTicket(null);
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const getFileDisplayFileName = (msg) => {
    const bodyTrim = msg.body && String(msg.body).trim();
    const urlBasename = msg.mediaUrl ? msg.mediaUrl.split("/").pop() : "";
    const bodyIsRealFilename = bodyTrim && /\.\w+$/.test(bodyTrim) && !bodyTrim.includes("@");
    return (bodyIsRealFilename ? bodyTrim : null) || urlBasename || bodyTrim || "File";
  };

  const isFileDocumentMessage = (msg) => {
    if (!msg.mediaUrl) return false;
    if (msg.mediaType === "location" || msg.mediaType === "vcard" || /\.webp$/i.test(msg.mediaUrl)) return false;
    if (msg.mediaType === "image" || msg.mediaType === "video" || msg.mediaType === "audio") return false;
    if (msg.mediaType?.startsWith("call")) return false;
    return true;
  };

  const getFileIcon = (displayFileName, mediaUrl) => {
    const name = (displayFileName || mediaUrl || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop().replace(/\?.*$/, "") : "";
    const iconProps = { className: classes.messageFileIcon };
    if (/pdf/i.test(ext) || name.endsWith(".pdf")) {
      return <PictureAsPdf {...iconProps} style={{ color: "#d32f2f" }} />;
    }
    if (/xlsx|xls|csv|ods/i.test(ext)) {
      return <TableChart {...iconProps} style={{ color: "#1e8e3e" }} />;
    }
    if (/docx?|odt|rtf/i.test(ext)) {
      return <Description {...iconProps} style={{ color: "#1a73e8" }} />;
    }
    if (/pptx?|odp/i.test(ext)) {
      return <Description {...iconProps} style={{ color: "#ea4335" }} />;
    }
    if (/zip|rar|7z|tar|gz/i.test(ext)) {
      return <InsertDriveFile {...iconProps} style={{ color: "#f9ab00" }} />;
    }
    if (/txt|md/i.test(ext)) {
      return <Description {...iconProps} />;
    }
    return <InsertDriveFile {...iconProps} />;
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body && message.body.split('|').length >= 2) {
      const locationParts = message.body.split('|').map((p) => p.trim());
      const latitude = locationParts[0];
      const longitude = locationParts[1];
      const description = locationParts.length > 2 ? locationParts.slice(2).join('|').trim() : null;
      return (
        <LocationPreview
          latitude={latitude}
          longitude={longitude}
          description={description || undefined}
        />
      );
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    else if (message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") {
      return <CallPreview message={message} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
      // WebP stickers: show as image regardless of mediaType (sent stickers may be "sticker", "document", or "image")
      else if (message.mediaUrl && /^.*\.webp$/i.test(message.mediaUrl)) {
        return (
          <div style={{ 
            background: 'transparent', 
            maxWidth: '200px',
            maxHeight: '200px',
            margin: '5px 0'
          }}>
            <img 
              src={message.mediaUrl} 
              alt="Sticker" 
              style={{ 
                width: '100%', 
                height: 'auto',
                objectFit: 'contain',
                background: 'transparent'
              }}
            />
          </div>
        );
      }
    else if ( /^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      // File/document: show icon by type, filename, and actions (preview for PDF, download).
      const displayFileName = getFileDisplayFileName(message);
      const isPdf = message.mediaUrl && /\.pdf$/i.test(message.mediaUrl);
      return (
        <>
          <div className={classes.downloadMedia}>
            <div className={classes.messageFileRow}>
              {getFileIcon(displayFileName, message.mediaUrl)}
              <div className={classes.messageFileName}>{displayFileName}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {isPdf && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => openPdfPreview(message.mediaUrl, displayFileName)}
                >
                  {i18n.t("messagesList.preview")}
                </Button>
              )}
              <Button
                size="small"
                startIcon={<GetApp />}
                color="primary"
                variant="outlined"
                target="_blank"
                href={message.mediaUrl}
              >
                {i18n.t("messagesList.download")}
              </Button>
            </div>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.nameUser || message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const isStickerMessage = (msg) => msg?.mediaUrl && /\.webp$/i.test(msg.mediaUrl);

  // Helper function to check if message should show avatar
  // Only show avatar for messages sent by us (fromMe). Client/incoming messages: no avatar.
  const shouldShowAvatar = (message, index) => {
    if (!message.fromMe) return false;

    const nextMessage = messagesList[index + 1];

    // Stickers (webp): show avatar only on the last sticker in a run from the same sender
    if (isStickerMessage(message)) {
      if (!nextMessage) return true;
      if (message.fromMe !== nextMessage.fromMe) return true;
      if (isStickerMessage(nextMessage)) return false;
      return true;
    }

    return true;
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        // WebP stickers (incoming): no bubble, sticker + timestamp on left; no avatar for client
        if (!message.fromMe && message.mediaUrl && /\.webp$/i.test(message.mediaUrl)) {
          const stickerContent = (
            <div
              style={{
                alignSelf: "flex-start",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginLeft: 0,
                marginRight: "20px",
                marginTop: "2px"
              }}
            >
              <img
                src={message.mediaUrl}
                alt="Sticker"
                style={{
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "contain",
                  background: "transparent"
                }}
              />
              <span style={{ fontSize: "11px", color: "#999", marginTop: "2px", marginLeft: "5px" }}>
                {format(parseISO(message.createdAt), "h:mm a")}
              </span>
            </div>
          );
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={clsx(highlightMessageId != null && String(highlightMessageId) === String(message.id) && classes.messageHighlighted)}
              style={{ width: "100%" }}
            >
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {wrapMessageForForward(message, stickerContent, false)}
            </div>
          );
        }

        // WebP stickers (sent by me): no bubble, sticker + timestamp + ack on right; avatar at bottom, only on last of run
        if (message.fromMe && message.mediaUrl && /\.webp$/i.test(message.mediaUrl)) {
          const showAvatar = shouldShowAvatar(message, index);
          const stickerContent = (
            <div
              style={{
                alignSelf: "flex-end",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                marginLeft: "20px",
                marginRight: 0,
                marginTop: "2px",
                gap: 8
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <img
                  src={message.mediaUrl}
                  alt="Sticker"
                  style={{
                    maxWidth: "150px",
                    maxHeight: "150px",
                    objectFit: "contain",
                    background: "transparent"
                  }}
                />
                <span style={{ fontSize: "11px", color: "#999", marginTop: "2px", marginRight: "5px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                  {format(parseISO(message.createdAt), "h:mm a")}
                  {renderMessageAck(message)}
                </span>
              </div>
              {showAvatar ? <MessageAvatar message={message} size={32} /> : <div style={{ width: 32, height: 32, flexShrink: 0 }} />}
            </div>
          );
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={clsx(highlightMessageId != null && String(highlightMessageId) === String(message.id) && classes.messageHighlighted)}
              style={{ width: "100%" }}
            >
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {wrapMessageForForward(message, stickerContent, true)}
            </div>
          );
        }

        // Special handling for call messages - render them outside the message bubble
        if (message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") {
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={clsx(highlightMessageId != null && String(highlightMessageId) === String(message.id) && classes.messageHighlighted)}
              style={{ width: "100%" }}
            >
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {wrapMessageForForward(message, checkMessageMedia(message), !message.fromMe ? false : true)}
            </div>
          );
        }

        // Regular message handling for non-stickers (incoming: no avatar)
        if (!message.fromMe) {
          const leftContent = (
            <div className={classes.messageLeft}>
              <div className={classes.messageLeftBubbleWrap}>
                {!forwardSelectMode && !message.isDeleted && (
                  <IconButton
                    variant="contained"
                    size="small"
                    id="reactionMessageButtonLeft"
                    className={classes.messageReactionButtonLeft}
                    onClick={(e) => handleReactionClick(e, message)}
                    title={i18n.t("messagesList.reactToMessage")}
                  >
                    <InsertEmoticon fontSize="small" />
                  </IconButton>
                )}
                <div className={classes.messageLeftContent}>
                  {!forwardSelectMode && (
                    <IconButton
                      variant="contained"
                      size="small"
                      id="messageActionsButton"
                      disabled={message.isDeleted}
                      className={classes.messageActionsButton}
                      onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    >
                      <ExpandMore />
                    </IconButton>
                  )}
                  {isGroup && (
                    <span className={classes.messageContactName}>
                      {message.nameUser || message.contact?.name}
                    </span>
                  )}
                  {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && 
                    !(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                    checkMessageMedia(message)}
                  <div className={classes.textContentItem}>
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {message.ctwaContext && <CtwaPreview context={message.ctwaContext} />}
                  {!(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                    (
                      // Regular text message or caption (for file docs only show body when it's a caption, not the filename)
                      (
                        (!(message.mediaType === "image" || message.mediaType === "sticker" || message.mediaType === "video" || message.mediaType === "audio") &&
                          message.mediaType !== "location") &&
                        (!isFileDocumentMessage(message) || (message.body && String(message.body).trim() !== getFileDisplayFileName(message)))
                      ) ||
                      // Caption for images (stickers/webp: never show body/filename)
                      (message.mediaType === "image" && !!message.body)
                    ) &&
                    !(message.mediaUrl && /\.webp$/i.test(message.mediaUrl)) && (
                      <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  )}
                    <span className={classes.timestamp}>
                      {format(parseISO(message.createdAt), "h:mm a")}
                    </span>
                  </div>
                </div>
              </div>
              <div className={clsx(classes.messageReactionsFloating, classes.messageReactionsFloatingLeft)}>
                {renderMessageReactions(message)}
              </div>
            </div>
          );
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={clsx(highlightMessageId != null && String(highlightMessageId) === String(message.id) && classes.messageHighlighted)}
              style={{ width: "100%" }}
            >
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {wrapMessageForForward(message, leftContent, false)}
            </div>
          );
        } else {
          const showAvatar = shouldShowAvatar(message, index);
          const rightContent = (
            <div className={classes.messageRight}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <div style={{ display: "flex", alignItems: "flex-end", marginLeft: 4 }}>
                  <div className={classes.messageRightBubbleWrap}>
                  {!forwardSelectMode && !message.isDeleted && (
                    <>
                      <IconButton
                        variant="contained"
                        size="small"
                        id="reactionMessageButton"
                        className={classes.messageReactionButtonFloating}
                        onClick={(e) => handleReactionClick(e, message)}
                        title={i18n.t("messagesList.reactToMessage")}
                      >
                        <InsertEmoticon fontSize="small" />
                      </IconButton>
                      <IconButton
                        variant="contained"
                        size="small"
                        id="editMessageButton"
                        className={classes.messageEditButtonFloating}
                        onClick={() => {
                          setMessageToEdit(message);
                          setEditDialogOpen(true);
                        }}
                        title={i18n.t("messagesList.editMessage")}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  <div className={classes.messageRightContent}>
                    {!forwardSelectMode && (
                      <IconButton
                        variant="contained"
                        size="small"
                        id="messageActionsButton"
                        disabled={message.isDeleted}
                        className={classes.messageActionsButton}
                        onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                      >
                        <ExpandMore />
                      </IconButton>
                    )}
                    {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && 
                      !(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                      checkMessageMedia(message)}
                    <div
                      className={clsx(classes.textContentItem, {
                        [classes.textContentItemDeleted]: message.isDeleted,
                      })}
                    >
                      {message.isDeleted && (
                        <Block
                          color="disabled"
                          fontSize="small"
                          className={classes.deletedIcon}
                        />
                      )}
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {message.ctwaContext && <CtwaPreview context={message.ctwaContext} />}
                    {!(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                      (
                        // Regular text message or caption (for file docs only show body when it's a caption, not the filename)
                        (
                          (!(message.mediaType === "image" || message.mediaType === "sticker" || message.mediaType === "video" || message.mediaType === "audio") &&
                            message.mediaType !== "location") &&
                          (!isFileDocumentMessage(message) || (message.body && String(message.body).trim() !== getFileDisplayFileName(message)))
                        ) ||
                        // Caption for images (stickers/webp: never show body/filename)
                        (message.mediaType === "image" && !!message.body)
                      ) &&
                      !(message.mediaUrl && /\.webp$/i.test(message.mediaUrl)) && (
                        <MarkdownWrapper>{message.body}</MarkdownWrapper>
                      )}
                      <span className={classes.timestamp}>
                        {format(parseISO(message.createdAt), "h:mm a")}
                        {renderMessageAck(message)}
                      </span>
                    </div>
                  </div>
                </div>
                  <div style={{ flexShrink: 0, width: 32, height: 32, marginLeft: 4 }}>
                    {showAvatar ? <MessageAvatar message={message} size={32} /> : null}
                  </div>
                </div>
                <div className={clsx(classes.messageReactionsFloating, classes.messageReactionsFloatingRight)}>
                  {renderMessageReactions(message)}
                </div>
              </div>
            </div>
          );
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={clsx(highlightMessageId != null && String(highlightMessageId) === String(message.id) && classes.messageHighlighted)}
              style={{ width: "100%" }}
            >
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {wrapMessageForForward(message, rightContent, true)}
            </div>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  const handleForward = (message) => {
    setForwardSelectedIds(new Set(message ? [message.id] : []));
    setForwardSelectMode(true);
    setAnchorEl(null);
  };

  const handleEditSave = async (newText) => {
    if (!messageToEdit) return;
    const { data } = await api.patch(`/messages/${messageToEdit.id}`, {
      text: newText,
      linkPreview: true,
      linkPreviewHighQuality: false
    });
    const updatedMessage = data
      ? { ...messageToEdit, ...data, body: data.body ?? newText }
      : { ...messageToEdit, body: newText };
    dispatch({ type: "UPDATE_MESSAGE", payload: updatedMessage });
  };

  const parseReactions = (reactionsJson) => {
    if (!reactionsJson || typeof reactionsJson !== "string") return {};
    try {
      const parsed = JSON.parse(reactionsJson);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  };

  const renderMessageReactions = (message) => {
    const reactions = parseReactions(message.reactions);
    const entries = Object.entries(reactions).filter(([, count]) => count > 0);
    if (entries.length === 0) return null;
    return (
      <div className={classes.messageReactionsRow}>
        {entries.map(([emoji, count]) => (
          <span key={emoji} className={classes.reactionBadge}>
            {emoji}
            {count > 1 && <span style={{ marginLeft: 2, fontSize: 11 }}>{count}</span>}
          </span>
        ))}
      </div>
    );
  };

  const handleReactionClick = (e, message) => {
    e.stopPropagation();
    setMessageForReaction(message);
    setReactionPickerAnchor(e.currentTarget);
  };

  const handleReactionSelect = async (emoji) => {
    if (!messageForReaction) return;
    const msgId = messageForReaction.id;
    const previousEmoji = ourReactionByMessageId[msgId];
    try {
      // If we already had a reaction and picked a different one, remove the old one first (switch)
      if (previousEmoji && previousEmoji !== emoji) {
        await api.post(`/messages/${msgId}/reaction`, {
          emoji: previousEmoji,
          remove: true,
        });
      }
      const { data } = await api.post(`/messages/${msgId}/reaction`, { emoji });
      if (data) dispatch({ type: "UPDATE_MESSAGE", payload: data });
      setOurReactionByMessageId((prev) => ({ ...prev, [msgId]: emoji }));
    } catch (err) {
      toastError(err);
    } finally {
      setReactionPickerAnchor(null);
      setMessageForReaction(null);
    }
  };

  const handleRemoveReaction = async () => {
    if (!messageForReaction) return;
    const emojiToRemove = ourReactionByMessageId[messageForReaction.id];
    if (!emojiToRemove) return;
    try {
      const { data } = await api.post(`/messages/${messageForReaction.id}/reaction`, {
        emoji: emojiToRemove,
        remove: true,
      });
      if (data) dispatch({ type: "UPDATE_MESSAGE", payload: data });
      setOurReactionByMessageId((prev) => {
        const next = { ...prev };
        delete next[messageForReaction.id];
        return next;
      });
    } catch (err) {
      toastError(err);
    } finally {
      setReactionPickerAnchor(null);
      setMessageForReaction(null);
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        onForward={handleForward}
      />
      <EditMessageDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setMessageToEdit(null);
        }}
        message={messageToEdit}
        onSave={handleEditSave}
      />
      <MessageReactionPicker
        open={Boolean(reactionPickerAnchor)}
        anchorEl={reactionPickerAnchor}
        onClose={() => {
          setReactionPickerAnchor(null);
          setMessageForReaction(null);
        }}
        onSelect={handleReactionSelect}
        onRemove={handleRemoveReaction}
        existingReactionFromUs={messageForReaction ? ourReactionByMessageId[messageForReaction.id] ?? null : null}
      />
      <ForwardMessagesDialog
        open={forwardDialogOpen}
        onClose={() => setForwardDialogOpen(false)}
        initialSelectedIds={forwardInitialSelectedIds}
        onForwardComplete={() => setForwardDialogOpen(false)}
      />
      <Dialog
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            maxWidth: "90vw",
            width: 800,
            height: "85vh",
            borderRadius: 12,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #eee" }}>
          <span style={{ fontSize: 16, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 16 }}>{pdfPreviewTitle}</span>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            href={pdfPreviewUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {i18n.t("messagesList.download")}
          </Button>
        </DialogTitle>
        <DialogContent style={{ padding: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {pdfPreviewUrl && (
            <iframe
              title={pdfPreviewTitle}
              src={`${pdfPreviewUrl}#toolbar=0`}
              style={{
                width: "100%",
                height: "100%",
                minHeight: 480,
                border: "none",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      {forwardSelectMode && (
        <div className={classes.forwardSelectionBar}>
          <Button size="small" onClick={cancelForwardSelection} color="default">
            {i18n.t("forward.cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={proceedToForwardRecipients}
            disabled={forwardSelectedIds.size === 0}
          >
            {i18n.t("forward.forwardSelected", { count: forwardSelectedIds.size })}
          </Button>
        </div>
      )}
      {onSearchClick && (
        <IconButton
          className={classes.searchButtonFloating}
          size="small"
          onClick={onSearchClick}
          title={i18n.t("tickets.search.inChat") || "Buscar en chat"}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;